// ./src/components/features/Forms/FormCodeDrawer/FormCodeDrawer.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  ActionIcon,
  Tooltip,
  Group,
  Box,
  CopyButton,
} from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";

import { Form } from "@/utils/api";
import { AppDrawer } from "@/components/generic";
import { SandboxCodePreview } from "@/components/features/SandboxCodePreview";
import { FormThemeSelector } from "@/components/features/Forms/FormThemeSelector";
import { DEFAULT_THEME } from "@/config";

const SCRIPTS_ROOT_URL =
  import.meta.env.VITE_SCRIPTS_ROOT_URL || "https://cdn.formsubmits.com";
const API_ROOT_URL =
  import.meta.env.VITE_SUBMISSION_API_URL_BASE || "https://api.formsubmits.com";

export interface FormCodeDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Form to display code for */
  form: Form | null;
}

export const FormCodeDrawer: React.FC<FormCodeDrawerProps> = ({
  opened,
  onClose,
  form,
}) => {
  const [selectedTheme, setSelectedTheme] = useState<string>(DEFAULT_THEME);
  const [generatedCode, setGeneratedCode] = useState<string>("");
  const [generatingCode, setGeneratingCode] = useState<boolean>(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);
  const [scriptError, setScriptError] = useState<string | null>(null);

  // Track if script is already being loaded
  const scriptLoadingRef = useRef<boolean>(false);

  // Build submission URL
  const submissionUrl = form?.path ? `${API_ROOT_URL}/submit/${form.path}` : "";

  // Load the FormSubmitsEmbed script
  useEffect(() => {
    if (!opened) return;

    // Check if already loaded
    if (window.FormSubmitsEmbed) {
      setScriptLoaded(true);
      return;
    }

    // Prevent duplicate loading
    if (scriptLoadingRef.current) {
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(
      `script[src*="formsubmits-embed.js"]`
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => {
        setScriptLoaded(true);
      });
      existingScript.addEventListener("error", () => {
        setScriptError("Failed to load form embed library");
      });
      return;
    }

    scriptLoadingRef.current = true;

    const script = document.createElement("script");
    script.src = `${SCRIPTS_ROOT_URL}/formsubmits-embed.js`;
    script.async = true;

    script.onload = () => {
      setScriptLoaded(true);
      scriptLoadingRef.current = false;
    };

    script.onerror = () => {
      setScriptError("Failed to load form embed library");
      scriptLoadingRef.current = false;
    };

    document.head.appendChild(script);
  }, [opened]);

  // Generate code when form path, theme, or script loading changes
  useEffect(() => {
    if (!opened || !scriptLoaded || !window.FormSubmitsEmbed || !form?.path) {
      return;
    }

    let cancelled = false;

    const generateCode = async () => {
      setGeneratingCode(true);
      setGenerationError(null);

      try {
        const code = await window.FormSubmitsEmbed!.generateHtml(form.path!, {
          theme: selectedTheme,
          submitLabel: "Submit",
          includeStyles: true,
          showDescription: true,
          apiUrl: API_ROOT_URL,
        });

        if (!cancelled) {
          setGeneratedCode(code);
        }
      } catch (error) {
        console.error("Failed to generate form HTML:", error);
        if (!cancelled) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to generate form";
          setGenerationError(errorMessage);
          setGeneratedCode("");
        }
      } finally {
        if (!cancelled) {
          setGeneratingCode(false);
        }
      }
    };

    generateCode();

    return () => {
      cancelled = true;
    };
  }, [opened, form?.path, selectedTheme, scriptLoaded]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!opened) {
      setGeneratedCode("");
      setGenerationError(null);
      setSelectedTheme(DEFAULT_THEME);
    }
  }, [opened]);

  // Handle theme change
  const handleThemeChange = useCallback((themeValue: string | null) => {
    if (themeValue) {
      setSelectedTheme(themeValue);
    }
  }, []);

  const isLoading = !scriptLoaded || generatingCode;
  const hasError = scriptError || generationError;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={form?.name ? `Get Code: ${form.name}` : "Get Form Code"}
      position="right"
      size="lg"
      actionType="close"
    >
      <Stack gap="lg" style={{ height: "100%", overflow: "hidden" }}>
        {/* Submission URL Section */}
        <Stack gap="xs" style={{ flexShrink: 0 }}>
          <Text fw={500} size="sm">
            Submission URL
          </Text>
          <Text size="xs" c="dimmed">
            Use this URL as the form action attribute to submit data to
            FormSubmits.
          </Text>
          <TextInput
            value={submissionUrl}
            readOnly
            size="md"
            placeholder="No form path available"
            rightSection={
              submissionUrl ? (
                <CopyButton value={submissionUrl}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied!" : "Copy URL"}>
                      <ActionIcon
                        variant={copied ? "filled" : "subtle"}
                        color={copied ? "green" : "gray"}
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconCopy size={16} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              ) : undefined
            }
          />
        </Stack>

        {/* HTML Code Section */}
        <Stack gap="xs" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Group
            justify="space-between"
            align="flex-end"
            style={{ flexShrink: 0 }}
          >
            <Box>
              <Text fw={500} size="sm">
                HTML Code
              </Text>
              <Text size="xs" c="dimmed">
                Copy or download the generated form HTML.
              </Text>
            </Box>
            <FormThemeSelector
              value={selectedTheme}
              onChange={handleThemeChange}
              size="sm"
              disabled={isLoading}
              style={{ minWidth: 200 }}
            />
          </Group>

          {/* Code Preview - takes remaining space */}
          <Box style={{ flex: 1, minHeight: 0 }}>
            <SandboxCodePreview
              code={generatedCode}
              loading={isLoading}
              error={hasError}
              downloadFilename={form?.name || "form"}
              formName={form?.name || undefined}
              theme={selectedTheme}
            />
          </Box>
        </Stack>
      </Stack>
    </AppDrawer>
  );
};
